import { message } from "antd";
import { useNavigate } from "react-router";
import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import api from "../services/api";
import apiXPBolsa from "../services/bolsa-api";
import apiXPExterior from "../services/exterior-api";

interface ILoginData {
  username: string;
  pass: string;
}

interface ISignUpData {
  email: string;
  pass: string;
  passConfirmation: string;
  username: string;
  usernameEscritorio: string;
  isAssessor: boolean;
  policiesAccepted: boolean;
}

interface IUser {
  name: string;
  email: string;
  username: string;
  fileLink?: string;
  office: boolean;
  admin: boolean;
  officeId?: string;
  assessorId?: string;
  isManager?: boolean;
  liberadaContratacao: boolean;
  discount?: number;
  hasPassword: boolean;
  enviarDarfAutomaticamente?: boolean;
  responsavel?: {
    name: string;
    phone: string;
  }
}

interface ISessionData extends IUser {
  token: string;
}

export interface IAuthState {
  token: string;
  user: IUser;
}

interface IAuthContext {
  handleSignIn: (loginData: ILoginData) => void;
  handleSignUp: (signUpData: ISignUpData) => void;
  setLocalStorageWithAuthData: (sesionData: ISessionData) => void;
  signOut: () => void;
  signed: boolean;
  user: IAuthState;
  apiError: string;
  loading: boolean;
  loadingSession: boolean;
  getUserInfo: () => void;
  updateUserInfo: () => void;
  updateUserDec: () => void;
  decUrl: string;
  showDrawer: boolean;
  showDbkModal: boolean;
  wasMessageSent: boolean;
  expandedSidebar: boolean;
  showIndicarModal: boolean;
  setDecUrl: Dispatch<SetStateAction<string>>;
  setUser: Dispatch<SetStateAction<IAuthState>>;
  setApiError: Dispatch<SetStateAction<string>>;
  setShowDrawer: Dispatch<SetStateAction<boolean>>;
  setShowDbkModal: Dispatch<SetStateAction<boolean>>;
  setWasMessageSent: Dispatch<SetStateAction<boolean>>;
  setExpandedSidebar: Dispatch<SetStateAction<boolean>>;
  setShowIndicarModal: Dispatch<SetStateAction<boolean>>;
}

export const AuthContext = createContext<IAuthContext>({} as IAuthContext);

export const AuthProvider: React.FC = ({ children }) => {
  const navigate = useNavigate();

  const [decUrl, setDecUrl] = useState("");
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showDbkModal, setShowDbkModal] = useState(false);
  const [loadingSession, setLoadingSession] = useState(true);
  const [wasMessageSent, setWasMessageSent] = useState(false);
  const [expandedSidebar, setExpandedSidebar] = useState(false);
  const [user, setUser] = useState<IAuthState>({} as IAuthState);
  const [showIndicarModal, setShowIndicarModal] = useState(false);

  const getUserInformation = async () => {
    const token = localStorage.getItem("@VeloTax:token");
    const user = localStorage.getItem("@VeloTax:user");
    if (user && token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      apiXPBolsa.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      apiXPExterior.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${token}`;

      const response = await api.get("/auth/userInfo");

      let userFromApi = null;
      if (response.data) {
        const {
          username,
          name,
          email,
          isOffice,
          assessorId,
          fileLink,
          officeId,
          isManager,
          isAdmin,
          liberadaContratacao,
          discount,
          hasPassword,
          enviarDarfAutomaticamente,
          responsavel
        } = response.data;
        userFromApi = {
          username,
          name,
          email,
          assessorId,
          fileLink,
          officeId,
          isManager,
          liberadaContratacao,
          discount,
          office: isOffice,
          admin: isAdmin,
          hasPassword,
          enviarDarfAutomaticamente,
          responsavel
        };
      }

      api.defaults.headers.common["Authorization"] = `Bearer ${
        response.headers.token || token
      }`;

      return {
        token: response.headers.token || token,
        user: userFromApi || JSON.parse(user),
      };
    }
    return {} as IAuthState;
  };

  const getUserInfo = useCallback((callback?: () => void) => {
    setLoadingSession(true);
    getUserInformation()
      .then((res) => {
        setUser(res);
        setLoadingSession(false);
        callback && callback();
      })
      .catch((err) => {
        console.log("err", err);
        setLoadingSession(false);
      });
  }, []);

  const updateUserInfo = useCallback(() => {
    getUserInformation().then((res) => {
      setUser(res);
    });
  }, []);

  const updateUserDec = useCallback(() => {
    const updatedUser = { ...user.user, answeredStepDec: true };
    const token = localStorage.getItem("@VeloTax:token") || "";

    setUser({ user: updatedUser, token });
  }, [user]);

  const signOut = useCallback(() => {
    setLoadingSession(true);
    localStorage.removeItem("@VeloTax:token");
    localStorage.removeItem("@VeloTax:user");
    api.defaults.headers.common["Authorization"] = "";
    setUser({} as IAuthState);
    navigate("/p/login");
    setTimeout(() => {
      window.location.reload();
    }, 500);
  }, [navigate]);

  useEffect(() => {
    api.interceptors.response.use(
      (response) => response,
      (error) => {
        console.log(error);
        if (error?.response?.status === 401) {
          signOut();
        }

        return Promise.reject(error);
      }
    );

    getUserInfo();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLocalStorageWithAuthData = useCallback(
    ({
      token,
      username,
      email,
      name,
      office,
      admin,
      fileLink,
      assessorId,
      officeId,
      isManager,
      liberadaContratacao,
      discount,
      hasPassword,
      enviarDarfAutomaticamente,
      responsavel
    }: ISessionData) => {
      localStorage.setItem("@VeloTax:token", token);
      localStorage.setItem(
        "@VeloTax:user",
        JSON.stringify({
          username,
          email,
          name,
          fileLink,
          officeId,
          assessorId,
          isManager,
          office,
          admin,
          liberadaContratacao,
          hasPassword,
        })
      );
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      setUser({
        token,
        user: {
          username,
          email,
          name,
          officeId,
          assessorId,
          isManager,
          fileLink,
          liberadaContratacao,
          discount,
          office,
          admin,
          hasPassword,
          enviarDarfAutomaticamente,
          responsavel
        },
      });
      navigate("/clients");
      setApiError("");
    },
    // eslint-disable-next-line
    []
  );

  const handleSignIn = useCallback(
    async (data: ILoginData) => {
      setLoading(true);
      try {
        const response: any = await api.post("/auth/login", {
          username: data.username,
          password: data.pass,
        });
        const {
          token,
          username,
          email,
          name,
          isOffice,
          isAdmin,
          fileLink,
          assessorId,
          officeId,
          isManager,
          liberadaContratacao,
          discount,
          hasPassword,
          enviarDarfAutomaticamente,
          responsavel
        } = response.data;

        setLoadingSession(true);
        setLocalStorageWithAuthData({
          token,
          username,
          email,
          name,
          assessorId,
          isManager,
          officeId,
          fileLink,
          liberadaContratacao,
          discount,
          office: isOffice,
          admin: isAdmin,
          hasPassword,
          enviarDarfAutomaticamente,
          responsavel
        });
        setLoading(false);
        setTimeout(() => {
          setLoadingSession(false);
        }, 500);
      } catch (err: any) {
        message.error(
          err.response?.data?.message ||
            "CPF e/ou senha incorretos. Tente novamente"
        );
        setLoading(false);
        // throw Error;
      }
    },
    [setLocalStorageWithAuthData]
  );

  const handleError = (err: any) => {
    message.error(
      err.response?.data?.message ||
        "Algo deu errado, tente novemente mais tarde"
    );
    setLoading(false);
  };

  const handleSignUp = useCallback(
    (signUpData) => {
      setLoading(true);
      const {
        name,
        username,
        email,
        pass,
        policiesAccepted,
        assessorId,
        isManager,
        isAssessor,
        usernameEscritorio,
      } = signUpData;

      const user = {
        name,
        username,
        email,
        assessorId,
        isManager,
        isAssessor,
        usernameEscritorio,
        password: pass,
        terms: policiesAccepted,
      };

      api
        .post("/accountants/register", user)
        .then((response) => {
          const { token, username, email, name, assessorId, isManager } =
            response.data;

          localStorage.removeItem("publiconboarding");
          localStorage.setItem("@VeloTax:token", token);
          localStorage.setItem(
            "@VeloTax:user",
            JSON.stringify({
              username,
              email,
              assessorId,
              isManager,
              name,
            })
          );
          api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

          getUserInfo(() => {
            navigate("/clients");
            setApiError("");
            setLoading(false);
          });
        })
        .catch((err) => handleError(err));
    },
    [navigate, getUserInfo]
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        decUrl,
        loading,
        signOut,
        setUser,
        apiError,
        setDecUrl,
        showDrawer,
        getUserInfo,
        setApiError,
        handleSignUp,
        handleSignIn,
        showDbkModal,
        updateUserDec,
        setShowDrawer,
        loadingSession,
        updateUserInfo,
        wasMessageSent,
        expandedSidebar,
        setShowDbkModal,
        showIndicarModal,
        setWasMessageSent,
        setExpandedSidebar,
        setShowIndicarModal,
        setLocalStorageWithAuthData,
        signed: !!Object.keys(user).length,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): IAuthContext => useContext(AuthContext);
